import { useRef, useState, type ReactNode } from 'react';

/**
 * A pinch / double-tap / wheel zoom-and-pan container.
 *
 * iOS standalone PWAs disable the browser's own pinch-to-zoom even when
 * the viewport allows it, so an attached image can't be zoomed natively.
 * This re-implements the gesture: two-finger pinch scales around the
 * midpoint, a single finger pans once zoomed in, double-tap toggles
 * zoom, and (for the desktop drag-drop browser) the wheel zooms around
 * the cursor.
 *
 * The inner wrapper is sized to exactly fill the pane and transformed
 * from its top-left, which lets us clamp the pan so the content can
 * never be flung off-screen and snaps back to centered at 1×.
 */

interface Point {
  x: number;
  y: number;
}
interface Transform {
  scale: number;
  x: number;
  y: number;
}

const MIN_SCALE = 1;
const MAX_SCALE = 6;
const DOUBLE_TAP_SCALE = 2.5;
const DOUBLE_TAP_MS = 300;

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

export function ZoomPane({ children }: { children: ReactNode }) {
  const paneRef = useRef<HTMLDivElement>(null);
  const [t, setT] = useState<Transform>({ scale: 1, x: 0, y: 0 });

  // Gesture bookkeeping kept in refs so it doesn't trigger re-renders.
  const pinch = useRef<{ dist: number; mid: Point; start: Transform } | null>(null);
  const pan = useRef<{ point: Point; start: Transform } | null>(null);
  const lastTap = useRef(0);

  function paneSize(): { w: number; h: number } {
    const r = paneRef.current?.getBoundingClientRect();
    return { w: r?.width ?? 1, h: r?.height ?? 1 };
  }

  function toPane(clientX: number, clientY: number): Point {
    const r = paneRef.current!.getBoundingClientRect();
    return { x: clientX - r.left, y: clientY - r.top };
  }

  /** Keep the scaled, top-left-anchored content covering the pane. */
  function clampTransform(next: Transform): Transform {
    const { w, h } = paneSize();
    const scale = clamp(next.scale, MIN_SCALE, MAX_SCALE);
    if (scale <= 1) return { scale: 1, x: 0, y: 0 };
    return {
      scale,
      x: clamp(next.x, -(scale - 1) * w, 0),
      y: clamp(next.y, -(scale - 1) * h, 0),
    };
  }

  /** New transform that holds the content point under `anchor` fixed. */
  function zoomAround(anchor: Point, nextScale: number, from: Transform): Transform {
    const cx = (anchor.x - from.x) / from.scale;
    const cy = (anchor.y - from.y) / from.scale;
    return clampTransform({
      scale: nextScale,
      x: anchor.x - cx * nextScale,
      y: anchor.y - cy * nextScale,
    });
  }

  function onTouchStart(e: React.TouchEvent) {
    if (e.touches.length === 2) {
      const a = toPane(e.touches[0]!.clientX, e.touches[0]!.clientY);
      const b = toPane(e.touches[1]!.clientX, e.touches[1]!.clientY);
      pinch.current = {
        dist: Math.hypot(a.x - b.x, a.y - b.y),
        mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
        start: t,
      };
      pan.current = null;
    } else if (e.touches.length === 1) {
      const p = toPane(e.touches[0]!.clientX, e.touches[0]!.clientY);
      // Double-tap toggles zoom at the tapped point.
      const now = e.timeStamp;
      if (now - lastTap.current < DOUBLE_TAP_MS) {
        lastTap.current = 0;
        setT((prev) =>
          prev.scale > 1.01
            ? { scale: 1, x: 0, y: 0 }
            : zoomAround(p, DOUBLE_TAP_SCALE, prev),
        );
        return;
      }
      lastTap.current = now;
      if (t.scale > 1) pan.current = { point: p, start: t };
    }
  }

  function onTouchMove(e: React.TouchEvent) {
    if (pinch.current && e.touches.length === 2) {
      e.preventDefault();
      const a = toPane(e.touches[0]!.clientX, e.touches[0]!.clientY);
      const b = toPane(e.touches[1]!.clientX, e.touches[1]!.clientY);
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const { start } = pinch.current;
      const scale = clamp((dist / pinch.current.dist) * start.scale, MIN_SCALE, MAX_SCALE);
      // Anchor on the original midpoint, then translate by how far the
      // fingers' midpoint has moved — pinch and two-finger drag in one.
      const cx = (pinch.current.mid.x - start.x) / start.scale;
      const cy = (pinch.current.mid.y - start.y) / start.scale;
      setT(
        clampTransform({
          scale,
          x: mid.x - cx * scale,
          y: mid.y - cy * scale,
        }),
      );
    } else if (pan.current && e.touches.length === 1) {
      e.preventDefault();
      const p = toPane(e.touches[0]!.clientX, e.touches[0]!.clientY);
      setT(
        clampTransform({
          scale: pan.current.start.scale,
          x: pan.current.start.x + (p.x - pan.current.point.x),
          y: pan.current.start.y + (p.y - pan.current.point.y),
        }),
      );
    }
  }

  function onTouchEnd(e: React.TouchEvent) {
    if (e.touches.length === 0) {
      pinch.current = null;
      pan.current = null;
    } else if (e.touches.length === 1) {
      // Lift from pinch to one-finger pan without a jump.
      pinch.current = null;
      const p = toPane(e.touches[0]!.clientX, e.touches[0]!.clientY);
      pan.current = t.scale > 1 ? { point: p, start: t } : null;
    }
  }

  function onWheel(e: React.WheelEvent) {
    e.preventDefault();
    const anchor = toPane(e.clientX, e.clientY);
    const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
    setT((prev) => zoomAround(anchor, clamp(prev.scale * factor, MIN_SCALE, MAX_SCALE), prev));
  }

  function onDoubleClick(e: React.MouseEvent) {
    const anchor = toPane(e.clientX, e.clientY);
    setT((prev) =>
      prev.scale > 1.01 ? { scale: 1, x: 0, y: 0 } : zoomAround(anchor, DOUBLE_TAP_SCALE, prev),
    );
  }

  return (
    <div
      ref={paneRef}
      className="zoom-pane"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
      onWheel={onWheel}
      onDoubleClick={onDoubleClick}
    >
      <div
        className="zoom-pane__content"
        style={{
          transform: `translate(${t.x}px, ${t.y}px) scale(${t.scale})`,
          transformOrigin: '0 0',
        }}
      >
        {children}
      </div>
    </div>
  );
}
